/**
 * Custom single-select dropdown. Replaces SlimSelect — no external dependencies.
 * Wraps a native <select> element (hidden, stays in DOM for form submission).
 */

const CHEVRON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 4.5L6 7.5L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const CHECK_SVG   = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2 6.5L4.5 9L10 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  return e;
}

export interface SelectOptions {
  placeholder?: string;
  searchPlaceholder?: string;
  openPosition?: "auto" | "up" | "down";
  onChange?: (value: string, label: string) => void;
}

interface Item { value: string; label: string; disabled: boolean; }

let _uid = 0;

export class Select {
  private readonly el: HTMLSelectElement;
  private readonly opts: SelectOptions;
  private container!: HTMLDivElement;
  private trigger!: HTMLButtonElement;
  private triggerVal!: HTMLSpanElement;
  private dropdown!: HTMLDivElement;
  private searchInput?: HTMLInputElement;
  private listEl!: HTMLUListElement;
  private allItems: Item[] = [];
  private isOpen = false;
  private activeIdx = -1;
  private readonly uid: string;

  constructor(target: string | HTMLSelectElement, opts: SelectOptions = {}) {
    const el = typeof target === "string"
      ? document.querySelector<HTMLSelectElement>(target)
      : target;
    if (!el) throw new Error("Select: element not found");
    this.el = el;
    this.opts = opts;
    this.uid = `cs${++_uid}`;
  }

  init(): this {
    if (this.el.dataset.csInited) return this;
    this._parse();
    this._build();
    this._wire();
    this.el.dataset.csInited = "1";
    return this;
  }

  getValue(): string { return this.el.value; }

  setValue(v: string): void {
    if (!Array.from(this.el.options).find(o => o.value === v)) return;
    this.el.value = v;
    this._updateLabel();
    this._refreshList();
    this.el.dispatchEvent(new Event("change", { bubbles: true }));
  }

  destroy(): void {
    document.removeEventListener("click", this._onDocClick, true);
    this.container?.remove();
    this.el.style.display = "";
    delete this.el.dataset.csInited;
  }

  private _parse(): void {
    this.allItems = Array.from(this.el.options)
      .filter(o => !(o.value === "" && (o.hasAttribute("data-placeholder") || o.text.trim() === "")))
      .map(o => ({ value: o.value, label: o.text.trim(), disabled: o.disabled }));
  }

  private _ph(): string {
    return this.opts.placeholder ?? this.el.dataset.placeholder ?? "선택하세요";
  }

  private _build(): void {
    this.el.style.display = "none";
    this.container = mk("div", { class: "cs-wrap" });

    // Trigger button
    this.trigger = mk("button", {
      type: "button", class: "cs-trigger",
      role: "combobox", "aria-haspopup": "listbox",
      "aria-expanded": "false", "aria-controls": `${this.uid}-lb`,
    }) as HTMLButtonElement;
    this.triggerVal = mk("span", { class: "cs-trigger__value" });
    const arrow = mk("span", { class: "cs-trigger__arrow", "aria-hidden": "true" });
    arrow.innerHTML = CHEVRON_SVG;
    this.trigger.append(this.triggerVal, arrow);
    this._updateLabel();

    // Dropdown panel
    this.dropdown = mk("div", {
      class: "cs-dropdown", id: `${this.uid}-lb`,
    }) as HTMLDivElement;
    this.dropdown.hidden = true;

    // Search (only when > 5 options)
    if (this.allItems.length > 5) {
      const wrap = mk("div", { class: "cs-dropdown__search" });
      this.searchInput = mk("input", {
        type: "text", class: "cs-dropdown__search-input",
        placeholder: this.opts.searchPlaceholder ?? "검색...",
        autocomplete: "off", "aria-label": "옵션 검색",
      }) as HTMLInputElement;
      wrap.appendChild(this.searchInput);
      this.dropdown.appendChild(wrap);
    }

    this.listEl = mk("ul", {
      class: "cs-list", role: "listbox", "data-lenis-prevent": "",
    }) as HTMLUListElement;
    this.dropdown.appendChild(this.listEl);
    this._buildList(this.allItems);

    this.container.append(this.trigger, this.dropdown);
    this.el.insertAdjacentElement("afterend", this.container);
  }

  private _updateLabel(): void {
    const sel = Array.from(this.el.options).find(o => o.selected && o.value !== "");
    if (sel) {
      this.triggerVal.textContent = sel.text.trim();
      this.triggerVal.classList.remove("cs-trigger__value--empty");
    } else {
      this.triggerVal.textContent = this._ph();
      this.triggerVal.classList.add("cs-trigger__value--empty");
    }
  }

  private _buildList(items: Item[]): void {
    this.listEl.innerHTML = "";
    const cur = this.el.value;

    if (!items.length) {
      const li = mk("li", { class: "cs-option cs-option--empty" });
      li.textContent = "결과 없음";
      this.listEl.appendChild(li);
      this.activeIdx = -1;
      return;
    }

    items.forEach(item => {
      const sel = item.value === cur;
      const cls = ["cs-option", sel && "cs-option--selected", item.disabled && "cs-option--disabled"]
        .filter(Boolean).join(" ");
      const li = mk("li", {
        class: cls, role: "option", "data-value": item.value,
        "aria-selected": String(sel),
        ...(item.disabled ? { "aria-disabled": "true" } : {}),
      });
      const lbl = mk("span", { class: "cs-option__label" });
      lbl.textContent = item.label;
      li.appendChild(lbl);
      if (sel) {
        const chk = mk("span", { class: "cs-option__check", "aria-hidden": "true" });
        chk.innerHTML = CHECK_SVG;
        li.appendChild(chk);
      }
      this.listEl.appendChild(li);
    });
    this.activeIdx = -1;
  }

  private _refreshList(): void {
    const q = this.searchInput?.value.toLowerCase() ?? "";
    this._buildList(q ? this.allItems.filter(i => i.label.toLowerCase().includes(q)) : this.allItems);
  }

  private _wire(): void {
    this.trigger.addEventListener("click", () => this.isOpen ? this._close() : this._open());
    this.trigger.addEventListener("keydown", e => this._onTriggerKey(e));
    this.searchInput?.addEventListener("input", () => this._refreshList());
    this.searchInput?.addEventListener("keydown", e => this._onNavKey(e));
    this.listEl.addEventListener("click", e => {
      const li = (e.target as Element).closest<HTMLElement>(".cs-option");
      if (!li || li.classList.contains("cs-option--disabled") || li.classList.contains("cs-option--empty")) return;
      this._pick(li.dataset.value ?? "");
    });
    document.addEventListener("click", this._onDocClick, true);
    this.el.addEventListener("change", () => { this._updateLabel(); this._refreshList(); });
  }

  private _open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this._position();
    this.dropdown.hidden = false;
    this.trigger.setAttribute("aria-expanded", "true");
    this.trigger.classList.add("cs-trigger--open");
    this._refreshList();
    requestAnimationFrame(() => {
      if (this.searchInput) { this.searchInput.value = ""; this.searchInput.focus(); }
      this.listEl.querySelector(".cs-option--selected")?.scrollIntoView({ block: "nearest" });
    });
  }

  private _close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.dropdown.hidden = true;
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.classList.remove("cs-trigger--open");
    this.activeIdx = -1;
    this._clearActive();
  }

  private _position(): void {
    const p = this.opts.openPosition ?? "auto";
    if (p !== "auto") { this.dropdown.classList.toggle("cs-dropdown--up", p === "up"); return; }
    const r = this.container.getBoundingClientRect();
    this.dropdown.classList.toggle("cs-dropdown--up", window.innerHeight - r.bottom < 220 && r.top > window.innerHeight - r.bottom);
  }

  private _pick(value: string): void {
    const item = this.allItems.find(i => i.value === value);
    if (!item || item.disabled) return;
    this.el.value = value;
    this._updateLabel();
    this._close();
    this.el.dispatchEvent(new Event("change", { bubbles: true }));
    this.opts.onChange?.(value, item.label);
  }

  private _onTriggerKey(e: KeyboardEvent): void {
    if ([" ", "Enter", "ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      if (!this.isOpen) { this._open(); return; }
    }
    if (e.key === "Escape") { this._close(); this.trigger.focus(); return; }
    if (e.key === "ArrowDown") this._moveActive(1);
    else if (e.key === "ArrowUp") this._moveActive(-1);
    else if (e.key === "Enter") this._confirmActive();
  }

  private _onNavKey(e: KeyboardEvent): void {
    if (e.key === "Escape") { this._close(); this.trigger.focus(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); this._moveActive(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); this._moveActive(-1); }
    else if (e.key === "Enter") { e.preventDefault(); this._confirmActive(); }
  }

  private _moveActive(dir: 1 | -1): void {
    const items = [...this.listEl.querySelectorAll<HTMLElement>(".cs-option:not(.cs-option--disabled):not(.cs-option--empty)")];
    if (!items.length) return;
    this._clearActive();
    this.activeIdx = Math.max(0, Math.min(items.length - 1, this.activeIdx + dir));
    items[this.activeIdx].classList.add("cs-option--active");
    items[this.activeIdx].scrollIntoView({ block: "nearest" });
  }

  private _confirmActive(): void {
    const items = [...this.listEl.querySelectorAll<HTMLElement>(".cs-option:not(.cs-option--disabled):not(.cs-option--empty)")];
    const a = items[this.activeIdx];
    if (a) this._pick(a.dataset.value ?? "");
  }

  private _clearActive(): void {
    this.listEl.querySelectorAll(".cs-option--active").forEach(e => e.classList.remove("cs-option--active"));
  }

  private _onDocClick = (e: MouseEvent): void => {
    if (!this.container.contains(e.target as Node)) this._close();
  };
}
